import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatAxiosError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const STATUS_META = {
  pending: { label: "En attente", className: "bg-muted text-muted-foreground" },
  dns_ok: { label: "DNS OK", className: "bg-blue-100 text-blue-700" },
  verified: { label: "Vérifié", className: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rejeté", className: "bg-red-100 text-red-700" },
};

export default function AdminSenderDomains() {
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState({});

  const load = () => api.get("/admin/sender-domains").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const setD = (id, patch) => setDraft((d) => ({ ...d, [id]: { ...d[id], ...patch } }));

  const saveDkim = async (id) => {
    setSaving((s) => ({ ...s, [id]: true }));
    try {
      await api.put(`/admin/sender-domains/${id}`, draft[id] || {});
      await load();
      toast.success("DKIM enregistré");
    } catch (e) { toast.error(formatAxiosError(e)); }
    finally { setSaving((s) => ({ ...s, [id]: false })); }
  };

  const validate = async (id) => {
    try {
      await api.post(`/admin/sender-domains/${id}/validate`);
      await load();
      toast.success("Domaine validé");
    } catch (e) { toast.error(formatAxiosError(e)); }
  };

  const reject = async (id) => {
    try {
      await api.post(`/admin/sender-domains/${id}/reject`, { note: draft[id]?.admin_note });
      await load();
      toast.success("Domaine rejeté");
    } catch (e) { toast.error(formatAxiosError(e)); }
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 max-w-4xl">
      <div className="mb-8">
        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-2">Administration</p>
        <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Domaines d'envoi</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Demandes de domaine expéditeur personnalisé par société — renseignez le DKIM, puis validez une fois le DNS vérifié (statut « DNS OK »).
        </p>
      </div>

      <div className="space-y-4" data-testid="admin-sender-domains">
        {rows.length === 0 && <p className="text-muted-foreground">Aucune demande.</p>}
        {rows.map((r) => {
          const meta = STATUS_META[r.status] || STATUS_META.pending;
          return (
            <div key={r.id} className="bg-card border border-border rounded-sm p-5 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <span className="font-mono font-semibold">{r.domain}</span>
                  <span className="text-xs text-muted-foreground"> · {r.companySlug}</span>
                </div>
                <span className={`text-[10px] uppercase tracking-wide px-2 py-1 rounded-sm font-semibold ${meta.className}`}>
                  {meta.label}
                </span>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <Input
                  defaultValue={r.dkimRecordName || ""}
                  onChange={(e) => setD(r.id, { dkim_record_name: e.target.value })}
                  placeholder="Nom DKIM (postal-…._domainkey)"
                  className="rounded-sm font-mono text-sm"
                />
                <Input
                  defaultValue={r.dkimRecordValue || ""}
                  onChange={(e) => setD(r.id, { dkim_record_value: e.target.value })}
                  placeholder="Valeur DKIM (v=DKIM1; p=…)"
                  className="rounded-sm font-mono text-sm"
                />
              </div>

              {r.adminNote && (
                <p className="text-xs text-muted-foreground">Note : {r.adminNote}</p>
              )}

              <div className="flex items-center gap-2 flex-wrap">
                <Button variant="outline" className="rounded-sm" disabled={saving[r.id]} onClick={() => saveDkim(r.id)}>
                  {saving[r.id] ? "Enregistrement…" : "Enregistrer le DKIM"}
                </Button>
                <Button
                  className="rounded-sm bg-primary hover:bg-primary/90"
                  disabled={r.status !== "dns_ok"}
                  onClick={() => validate(r.id)}
                  data-testid={`validate-${r.id}`}
                >
                  Valider
                </Button>
                <button className="text-xs text-muted-foreground hover:text-destructive ml-auto" onClick={() => reject(r.id)}>
                  Rejeter
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
